import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { settingsApi, type TenantSettings } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  NumberField,
  Panel,
  ReadOnlyField,
  SaveErrorNotice,
  useSaveErrors,
  useSaveFailureFocus,
  useServerForm,
  useToast,
} from '../../components/ui/index.js';

const fromServer = (source: TenantSettings) => ({
  equipmentNoticeDays: source.equipmentNoticeDays as number | null,
});

/**
 * Notification settings of the laboratory — Phase 12 (D173).
 *
 * `equipmentNoticeDays` is how many days ahead a calibration or planned
 * maintenance date raises a notification. The backend's default for a new
 * laboratory lives only in the backend; this page shows what the server
 * reports and never restates it (CLAUDE.md section 6.1). Saved on its own.
 */
export function NotificationSettingsPanel({ settings }: { readonly settings: TenantSettings }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();

  const canEdit = can(PERMISSIONS.SETTINGS_UPDATE);

  // Authoritative while edited, reset only by this panel's own success (D205).
  const draft = useServerForm(settings, fromServer, fromServer(settings));
  const equipmentNoticeDays = draft.values.equipmentNoticeDays;

  const save = useMutation({
    mutationFn: ({ days }: { days: number | null; mark: number }) =>
      settingsApi.update({ equipmentNoticeDays: days ?? settings.equipmentNoticeDays }),
    onSuccess: async (saved, { mark }) => {
      draft.saved(saved, mark);
      queryClient.setQueryData(['settings'], saved);
      show({ tone: 'success', message: t('settings.savedNotice') });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
  const { fields: serverErrors } = useSaveErrors(save.error);
  const panelRef = useRef<HTMLDivElement>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  useSaveFailureFocus(save.error, panelRef, noticeRef);

  return (
    <Panel
      title={t('settings.notifications.title')}
      description={t('settings.notifications.description')}
    >
      <div ref={panelRef} className="flex flex-col gap-gutter">
        {canEdit ? (
          <NumberField
            label={t('settings.notifications.equipmentNoticeDays')}
            description={t('settings.notifications.equipmentNoticeDaysHint')}
            value={equipmentNoticeDays}
            onChange={(value) => draft.set('equipmentNoticeDays', value)}
            {...(serverErrors.equipmentNoticeDays ? { error: serverErrors.equipmentNoticeDays } : {})}
          />
        ) : (
          <ReadOnlyField label={t('settings.notifications.equipmentNoticeDays')}>
            <span dir="ltr">{settings.equipmentNoticeDays}</span>
          </ReadOnlyField>
        )}

        {save.error ? (
          <div ref={noticeRef} tabIndex={-1} className="outline-none">
            <SaveErrorNotice error={save.error} title={t('settings.saveFailed')} />
          </div>
        ) : null}

        {canEdit ? (
          <ActionBar>
            <ActionButton
              priority="primary"
              isLoading={save.isPending}
              onPress={() => save.mutate({ days: equipmentNoticeDays, mark: draft.mark() })}
            >
              {t('settings.save')}
            </ActionButton>
          </ActionBar>
        ) : null}
      </div>
    </Panel>
  );
}
